import { useMemo, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type CLEN } from "@/models.ts";
import { type CHLD } from "@/models/chld";
import { useLeaData } from "@/queries.ts";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { FiPlus } from "react-icons/fi";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { getClassroomName } from "@/utils/classroom-lookup";
import CLENEditDialog from "./dialogs/CLENEditDialog";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  { id: "ClassroomName", field: "ClassroomName", title: "Classroom" },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Classroom", key: "ClassroomName" },
];

type CLENRow = CLEN & {
  PreschoolName?: string;
  ChildName?: string;
  ClassroomName?: string;
  __raw: CLEN;
};

export default function CLENRecordsTab({
  clenRecords,
  child,
}: {
  clenRecords: CLEN[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [popupCLEN, setPopupCLEN] = useState<CLEN | undefined>(undefined);
  const [clenDefaults, setClenDefaults] = useState<Partial<CLEN> | undefined>(
    undefined
  );

  const canEdit = leaContext.hasRole("CLENEdit");

  const closeDialog = () => {
    setDialogOpen(false);
    setPopupCLEN(undefined);
    setClenDefaults(undefined);
  };

  const onAddButtonClick = () => {
    const defaults: Partial<CLEN> = {
      LocalChildId: child.LocalChildId ?? "",
      PreschoolCode: child.PreschoolCode ?? "",
      ProgramYear: child.ProgramYear ?? "",
      CSPPID: child.CSPPID ?? "",
      LocalClassroomId: "",
    };
    setClenDefaults(defaults);
    setPopupCLEN(undefined);
    setDialogOpen(true);
  };

  const onEditButtonClick = (record: CLEN) => {
    setClenDefaults(undefined);
    setPopupCLEN(record);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData: CLENRow[] = useMemo(() => {
    return (clenRecords ?? []).map((record) => ({
      ...record,
      ChildName: childName,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      ClassroomName: getClassroomName(
        leaData,
        record.PreschoolCode,
        record.ProgramYear,
        record.LocalClassroomId
      ),
      __raw: record,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clenRecords, leaContext?.currentOrgPreschools, childName, leaData]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export classroom enrollment records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(
              ["ChildName"],
              "classroom-enrollment-record"
            )}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add a classroom enrollment record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) =>
          onEditButtonClick((event.dataItem as CLENRow).__raw)
        }
      />

      {dialogOpen && (
        <CLENEditDialog
          selectedCLEN={popupCLEN}
          clenDefaults={clenDefaults}
          child={child}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
